import React, { useState } from 'react';
import styled from 'styled-components';

import {
  CONTENTFUL_COMPONENT,
  CONTENTFUL_INPUT_TYPES,
  InputType,
  POSITIONS,
} from '../../../shared/constants';
import {
  IFormInput,
  IRadioButton,
  IWizardCheckbox,
  IWizardFormAnswers,
} from '../../../types/shared';

import Checkbox from '../../atoms/checkbox/Checkbox';
import EnrichedCheckbox from '../../atoms/enriched-checkbox/EnrichedCheckbox';
import EnrichedRadiobutton from '../../atoms/enriched-radiobutton/EnrichedRadiobutton';
import FieldWrapper from '../../atoms/field-wrapper/FieldWrapper';
import FormInput from '../../atoms/form-input/FormInput';
import FormRadiobutton from '../../atoms/form-radio-button/FormRadiobutton';

import { validateForm } from '../../../utils/forms/formValidation';
import {
  DEFAULT_ERROR,
  FORM_LABEL,
  RADIO_ERROR,
} from '../../../utils/forms/translationKeys';
import { ITranslationItem } from '../../../utils/language';
import languageHelper from '../../../utils/language/language-helper';

import Colors from '../../../styles/colors';
import { Label, LabelLight, Legend, SmallText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import ErrorMessage from '../error-message/ErrorMessage';

interface Props {
  title?: string;
  name: string;
  isRequired?: boolean;
  fields?: IRadioButton[] | IWizardFormAnswers[] | IFormInput[];
  layout?: string;
  inputStyle?: string;
  helperMessage?: string;
  customCheckboxHandler?: (name: string, isChecked: boolean) => void;
  customRadioHandler?: (event: React.MouseEvent<HTMLInputElement>) => void;
  className?: string;
  hideRadioInput?: boolean;
}

const FormFieldset = ({
  title,
  name,
  isRequired,
  fields,
  layout,
  inputStyle,
  helperMessage,
  customCheckboxHandler,
  customRadioHandler,
  className,
  hideRadioInput,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<ITranslationItem>();
  const [value, setValue] = useState('');
  const { lang } = languageHelper();

  const onValueChangeHandler = (
    validity: ValidityState,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLInputElement>
      | React.FormEvent<HTMLInputElement>,
    customError?: string,
    type?: string
  ) => {
    if (event) setValue(event.currentTarget.value);
    if (type === CONTENTFUL_INPUT_TYPES.radio) {
      setErrorMessage(validateForm(validity, title || '', RADIO_ERROR));
    }
    if (!type) {
      setErrorMessage(
        validateForm(
          validity,
          customError || title || '',
          !customError ? DEFAULT_ERROR : undefined
        )
      );
    }
  };

  return (
    <StyledFieldset data-testid="form-fieldset" className={className}>
      {title && (
        <LegendWrapper>
          <StyledLegend>{title}</StyledLegend>
          {!isRequired && (
            <StyledLabelLight>{` ${FORM_LABEL.required[lang]}`}</StyledLabelLight>
          )}
        </LegendWrapper>
      )}
      <FieldWrapper
        layout={layout}
        fieldsetType={inputStyle}
        fitContainer={!!hideRadioInput}
      >
        {fields?.map((item, index) => {
          if (
            item.type === CONTENTFUL_INPUT_TYPES.radio &&
            inputStyle === InputType.ENRICHED_RADIO
          ) {
            return (
              <EnrichedRadiobutton
                key={index}
                id={item.label}
                name={name}
                value={item.label}
                label={item.label}
                error={errorMessage && errorMessage[lang]}
                isRequired={isRequired}
                isChecked={item.label === value}
                onInvalidHandler={onValueChangeHandler}
                onClick={customRadioHandler}
                image={item?.image}
                layout={layout}
                hideRadioCircle={hideRadioInput}
              />
            );
          }
          if (
            item.type === CONTENTFUL_INPUT_TYPES.radio &&
            inputStyle !== InputType.ENRICHED_RADIO
          ) {
            return (
              <FormRadiobutton
                key={index}
                id={`${name}-${index}`}
                name={name}
                label={item.label}
                error={errorMessage && errorMessage[lang]}
                isRequired={isRequired}
                value={item.value || item.label}
                onInvalidHandler={onValueChangeHandler}
                tooltip={item.tooltip}
              />
            );
          }
          if (
            item.__typename === CONTENTFUL_COMPONENT.wizardCheckbox &&
            inputStyle === InputType.DEFAULT_CHECKBOX
          ) {
            return (
              <Checkbox
                key={index}
                id={`${inputStyle}_${item.id}`}
                name={item.valueId || `${item.label}-${item.id}`}
                label={item?.label}
                labelAdvanced={
                  (item as IWizardCheckbox)?.labelAdvanced?.childMarkdownRemark
                    ?.rawMarkdownBody
                }
                title={(item as IWizardCheckbox)?.descriptionLabel}
                helperMessage={(item as IWizardCheckbox)?.helperMessage}
                tooltip={item?.tooltip}
                customOnChange={customCheckboxHandler && customCheckboxHandler}
                isOptionGroup
              />
            );
          }
          if (
            item.__typename === CONTENTFUL_COMPONENT.wizardCheckbox &&
            inputStyle === InputType.ENRICHED_CHECKBOX
          ) {
            return (
              <EnrichedCheckbox
                key={index}
                id={`${inputStyle}_${item.id}`}
                name={item.valueId || `${item.label}-${item.id}`}
                label={item.label}
                title={(item as IWizardCheckbox)?.descriptionLabel}
                helperMessage={(item as IWizardCheckbox)?.helperMessage}
                tooltip={item?.tooltip}
                image={item?.image}
                customOnChange={customCheckboxHandler && customCheckboxHandler}
                isOptionGroup
              />
            );
          }
          return (
            <FormInput
              key={index}
              id={(item as IFormInput).id}
              name={(item as IFormInput).valueId}
              label={item.label}
              error={errorMessage && errorMessage[lang]}
              placeholder={(item as IFormInput).placeholder}
              required={(item as IFormInput).required}
              onInvalidHandler={onValueChangeHandler}
              type={item.type}
              tooltip={item?.tooltip}
              tooltipOrientation={POSITIONS.right}
              helperMessage={(item as IFormInput).helperMessage}
            />
          );
        })}
      </FieldWrapper>
      {helperMessage && <StyledSmallText>{helperMessage}</StyledSmallText>}
      {errorMessage && (
        <StyledErrorMessage>{errorMessage[lang]}</StyledErrorMessage>
      )}
    </StyledFieldset>
  );
};

export default FormFieldset;

const StyledFieldset = styled.fieldset`
  border: none;
  margin-left: 0;
  margin-right: 0;
  padding: 0;
  width: 100%;
`;

const StyledLegend = styled(Legend)`
  color: ${Colors.abnMutedGreen400};
  font-size: 14px;
  margin-bottom: ${Spacings.XS};
`;

const StyledErrorMessage = styled(ErrorMessage)`
  width: 100%;
`;

const LegendWrapper = styled(Label)`
  display: inline-flex;
`;

const StyledLabelLight = styled(LabelLight)`
  color: ${({ theme }) => theme.colors.abnNeutral300};
  font-family: ${({ theme }) => theme.fontRegular};
  margin-left: 4px;
`;

const StyledSmallText = styled(SmallText)`
  color: ${Colors.abnNeutral400};
  margin-top: ${Spacings.XS};
`;
